/**
 * Tipos de dominio para promociones POS (motor offline, paridad con pos.promotions).
 */

export type PromotionKind =
  | 'percent_off'
  | 'fixed_off'
  | 'combo_buy_x_get_y'
  | 'combo_n_for_price'
  | 'manual';

export type PromotionScope = 'line' | 'category' | 'cart';

export type PromotionApplyMode = 'automatic' | 'manual_code' | 'cashier_select';

/** Canal de venta donde aplica la promo (`pos.promotions.channels`). */
export type PromotionChannel = 'pos' | 'online';

/** Default: válida en POS y online (paridad 5B.1). */
export const DEFAULT_PROMOTION_CHANNELS: PromotionChannel[] = ['pos', 'online'];

export type PromotionTargetType = 'product' | 'category' | 'brand' | 'variant';

export type PromotionTarget = {
  id: string;
  promotionId: string;
  targetType: PromotionTargetType;
  targetId: string;
  createdAt?: string | null;
};

/** Días de la semana para scheduling (1=lunes … 7=domingo, ISO 8601). */
export type Weekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/** Ventana horaria para una promo (formato HH:mm). */
export type ScheduleWindow = {
  startTime: string;
  endTime: string;
};

/** Condiciones avanzadas para una promoción. */
export type PromotionConditions = {
  /** Monto mínimo de compra en el carrito. */
  minPurchaseAmount?: number | null;
  /** Cantidad mínima de unidades en el carrito. */
  minQuantity?: number | null;
  /** Límite total de usos (null = ilimitado). */
  usageLimit?: number | null;
  /** Días de la semana en que la promo está activa (vacío = todos). */
  weekdays?: Weekday[] | null;
  /** Ventana horaria para la promo (null = todo el día). */
  scheduleWindow?: ScheduleWindow | null;
};

export type Promotion = {
  id: string;
  organizationId: string;
  name: string;
  description?: string | null;
  kind: PromotionKind;
  scope: PromotionScope;
  valuePercent?: number | null;
  valueFixed?: number | null;
  comboBuyQty?: number | null;
  comboPayQty?: number | null;
  comboBundlePrice?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  isActive: boolean;
  branchIds?: string[] | null;
  /** Canales donde aplica; vacío/ausente = ambos (paridad legacy). */
  channels?: PromotionChannel[] | null;
  applyMode: PromotionApplyMode;
  promoCode?: string | null;
  priority: number;
  stackable: boolean;
  /** Condiciones avanzadas (ERP-like). */
  conditions?: PromotionConditions | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  targets?: PromotionTarget[];
};

/** Línea de carrito con precio unitario antes de promos automáticas. */
export type PricedCartLine = {
  lineId: string;
  productId: string;
  quantity: number;
  unitPrice: number;
  categoryId?: string | null;
  brandId?: string | null;
  /** Variante comprada (talle, color…). Target `variant` matchea contra este id. */
  variantId?: string | null;
  /** Excluido del motor automático cuando es `custom` o `package` (precio definido por el paquete). */
  itemType?: 'product' | 'custom' | 'package';
  /** Productos misc / varios sin promo automática. */
  isMisc?: boolean;
};

export type AppliedDiscount = {
  promotionId: string;
  promotionName: string;
  kind: PromotionKind;
  scope: PromotionScope;
  lineId?: string;
  discountAmount: number;
  /** Unidades afectadas (combos). */
  quantityAffected?: number;
};

export type EvaluatedCartLine = {
  lineId: string;
  productId: string;
  quantity: number;
  unitPriceOriginal: number;
  unitPrice: number;
  lineSubtotal: number;
  lineDiscountTotal: number;
  appliedDiscounts: AppliedDiscount[];
};

export type CartEvaluationResult = {
  lines: EvaluatedCartLine[];
  subtotal: number;
  discountTotal: number;
  total: number;
  cartDiscounts: AppliedDiscount[];
};

export type PromotionWithTargets = Promotion & {
  targets: PromotionTarget[];
};

export type PromotionUpsertInput = {
  name: string;
  description?: string | null;
  kind: PromotionKind;
  scope: PromotionScope;
  valuePercent?: number | null;
  valueFixed?: number | null;
  comboBuyQty?: number | null;
  comboPayQty?: number | null;
  comboBundlePrice?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  isActive?: boolean;
  branchIds?: string[] | null;
  applyMode?: PromotionApplyMode;
  promoCode?: string | null;
  priority?: number;
  stackable?: boolean;
  channels?: PromotionChannel[];
  conditions?: PromotionConditions | null;
  targets?: Array<{
    id?: string;
    targetType: PromotionTargetType;
    targetId: string;
  }>;
};
